import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  Radio,
  SimpleGrid,
  Skeleton,
  Spacer,
  Stack,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Formik, FormikProps } from 'formik';
import * as _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { date as commonDate } from '@finley-technologies/common';
import { Bank, Util } from '@finley-technologies/finley-types';
import {
  FinleyValleyBankResponseTapeRow,
  generateCollateralAccounts,
  getAdvanceRate,
  getCollateralAccountsTotalLendableValue,
  getCollateralAccountsTotalMarketValue,
  getCollateralDescription,
  getPreAllocationLendableValue,
  getPreAllocationMarketValue,
  getQuantity,
  getSecurityType,
  groupReceivableResponseTape,
  parse,
  ValleyBankLoanAccountWithCollateralAndStats,
  generateCollateralAccountsForLoanAccount,
  calculateMarginAvailabilityRate,
  calculateMarginAvailability,
  getCollateralAccountStats,
} from '@finley-technologies/valley-bank-utils';

import {
  GetValleyBankAssignmentEventLogQueryGQLType,
  useAddToValleyBankAssignmentQueueMutation,
  useDeleteFromValleyBankAssignmentQueueMutation,
  useGetUserQuery,
  useGetValleyBankAssignmentDeviationQuery,
  useGetValleyBankAssignmentEventLogQuery,
  useGetValleyBankAssignmentPreviewRrtLazyQueryGQLType,
  useGetValleyBankAssignmentQueueQuery,
  useGetValleyBankAssociatedLoanAccountsByCifNumsLazyQueryGQLType,
  useGetValleyBankLoanAccountsByAccountNumsQuery,
  useSubmitValleyBankAssignmentQueueMutation,
  ValleyBankAddToAssignmentQueueGQLType,
  ValleyBankAssignmentQueueWithNameGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  getUnexpectedErrorMessage,
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import { colors } from '~/packages/common-frontend/colors';
import { Alert } from '~/packages/common-frontend/components/Alert';
import { DeleteIconButton } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { SearchSelect } from '~/packages/common-frontend/components/SearchSelect';
import {
  formatSelectOptions,
  Select,
} from '~/packages/common-frontend/components/Select';
import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { ArrangementGroupSummaryLink } from '~/valleyBank/domains/ArrangementGroups/hooks/ArrangementGroupsContext';
import { CollateralTable } from '~/valleyBank/domains/Collateral/ui/HoldingsTable';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { LoanAccountSummaryLink } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';
import { useUserRoleTypePermissionsContext } from '~/valleyBank/domains/page/hooks/useUserRoleTypePermissions';

const COLLATERAL_ACCOUNT_HEADERS = [
  'Collateral Accounts to Assign',
  'Customer',
  'Arrangement Group',
  'Assignment Status',
];

const ASSIGNABLE_LOAN_ACCOUNT_HEADERS = [
  'Loan Account',
  'Borrower',
  'Arrangement Group',
  'Outstanding Balance',
  'Collateral Lendable Value',
  'Margin Availability',
  'Margin Availability Rate',
];

const IMPACTED_LOAN_ACCOUNT_HEADERS = [
  'Loan Account',
  'Arrangement Group',
  'Outstanding Balance',
  'Collateral Market Value',
  'Allocation Percentage',
  'Collateral Lendable Value',
  'Margin Availability',
  'Margin Availability Rate',
];

const PREVIEW_HOLDINGS_PER_LOAN_ACCOUNT_HEADERS = [
  'Collateral Description',
  'Collateral Account',
  'Security Type',
  'Market Value',
  'Advance Rate',
  'Lendable Value',
  'Quantity',
];

const tabProps = {
  marginTop: 0,
  marginRight: 0,
  fontSize: 'sm',
  _selected: {
    color: 'blue.400',
    borderBottomColor: 'blue.400',
    borderBottomWidth: '4px',
    boxShadow: 'none',
  },
};

const ASSIGNMENT_URL_PARAM = 'queue';
export const generateAssignmentUrl = (showQueue: boolean) => {
  return `/valley-bank/assignment?${ASSIGNMENT_URL_PARAM}=${showQueue.valueOf()}`;
};

const convertErrorToString = (e: any): string => {
  // if there was a message there return it, otherwise stringify.
  const message: string = 'message' in e ? e.message : JSON.stringify(e);
  return message;
};

type ManuallyAddCollateralAccountsProps = {
  searchOptions: { label: string; value: string }[];
  onSubmit: (val: string) => void;
};
const ManuallyAddCollateralAccounts = ({
  searchOptions,
  onSubmit,
}: ManuallyAddCollateralAccountsProps) => {
  return (
    <>
      <Formik
        initialValues={{
          collateralAccountNumber: '',
        }}
        onSubmit={(props) => {
          if (props.collateralAccountNumber) {
            onSubmit(props.collateralAccountNumber);
          }
        }}
        key={uuidv4()}
      >
        {(localFormProps: FormikProps<{ collateralAccountNumber: string }>) => {
          return (
            <Box bg={colors.millet[50]} p="16px" borderRadius="8px">
              <Box width="full" bg="white">
                <SearchSelect
                  placeholder="Search by Collateral Account"
                  options={searchOptions}
                  value={searchOptions.find(
                    (option) =>
                      option.value ===
                      localFormProps.values.collateralAccountNumber,
                  )}
                  onChange={(value) => {
                    localFormProps.setFieldValue(
                      'collateralAccountNumber',
                      value,
                    );
                    localFormProps.handleSubmit();
                  }}
                  displaySearchIcon
                />
              </Box>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};

type ConfirmSubmitModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};
const ConfirmSubmitModal = ({
  isOpen,
  onCancel,
  onSubmit,
}: ConfirmSubmitModalProps) => {
  const cancelRef = React.useRef(null);
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Submit Assignment
          </AlertDialogHeader>

          <AlertDialogBody>
            <>
              <Text mb={2}>Are you sure you want to submit assignment?</Text>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    onClick={() => {
                      onSubmit();
                    }}
                    px="20px"
                  >
                    Submit
                  </Button>
                </HStack>
              </Box>
            </>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

type ViewAssignmentQueueDrawerProps = {
  isOpen: boolean;
  queue: ValleyBankAssignmentQueueWithNameGQLType[];
  onClose: () => void;
  onSubmit: () => void;
  onDelete: (assignmentQueueId: string) => void;
  isDeleteLoading: boolean;
  isSubmitLoading: boolean;
  canEditAssignmentQueue: boolean;
  canSubmitAssignment: boolean;
};
const ViewAssignmentQueueDrawer = ({
  isOpen,
  queue,
  onClose,
  onSubmit,
  onDelete,
  isDeleteLoading,
  isSubmitLoading,
  canSubmitAssignment,
  canEditAssignmentQueue,
}: ViewAssignmentQueueDrawerProps) => {
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState<boolean>(false);

  return (
    <>
      <ConfirmSubmitModal
        isOpen={isSubmitModalOpen}
        onCancel={() => {
          setIsSubmitModalOpen(false);
        }}
        onSubmit={() => {
          onSubmit();
          setIsSubmitModalOpen(false);
        }}
      />
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton _focus={{ boxShadow: 'none' }} />
          <DrawerBody ml={3}>
            <Flex flexDirection="column" height="full">
              <Heading
                as="h1"
                mt="16px"
                mb="16px"
                fontSize="25px"
                fontWeight={600}
              >
                Assignment Summary
              </Heading>
              <Text fontSize="14px" mb="24px" color={colors.black[70]}>
                Today&apos;s queued assignments will automatically process the
                following day at 1 AM ET, initiating portfolio-wide advance rate
                recalculations. The site will be unavailable at this time.
              </Text>

              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color="black" fontSize="14px" fontWeight={500}>
                      User
                    </Th>
                    <Th color="black" fontSize="14px" fontWeight={500}>
                      Queued At
                    </Th>
                    <Th color="black" fontSize="14px" fontWeight={500}>
                      Collateral Account
                    </Th>
                    <Th color="black" fontSize="14px" fontWeight={500}>
                      Loan Account
                    </Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {queue.length === 0 ? (
                    <Tr>
                      <Td colSpan={3} color={colors.black[70]} fontSize="14px">
                        There are no assignment in queue.
                      </Td>
                    </Tr>
                  ) : (
                    queue.map((q) => {
                      return (
                        <Tr
                          key={`${q.collateralAccountNumber}-${q.loanAccountNumber}-${q.collateralAccountType}`} // should never be duplicative
                        >
                          <Td>{`${_.capitalize(q.userFirstName)} ${_.capitalize(
                            q.userLastName,
                          )}`}</Td>
                          <Td>
                            {commonDate.epochToLocaleDateString(
                              q.created_at,
                              undefined,
                            )}
                          </Td>
                          <Td>{q.collateralAccountNumber}</Td>
                          {q.action === 'assign' ? (
                            <Td>
                              <LoanAccountSummaryLink
                                key={q.loanAccountNumber}
                                loanAccountId={q.loanAccountNumber}
                              >
                                {q.loanAccountNumber}
                              </LoanAccountSummaryLink>
                            </Td>
                          ) : (
                            <Td>{`Unassign from ${q.loanAccountNumber}`}</Td>
                          )}
                          <Td>
                            <DeleteIconButton
                              label="Delete"
                              ariaLabel="delete"
                              onClick={() => onDelete(q.id)}
                              isLoading={isDeleteLoading}
                              isDisabled={!canEditAssignmentQueue}
                            />
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>

              <Spacer />

              <>
                <Divider style={{ marginTop: '24px', marginBottom: '24px' }} />

                {canSubmitAssignment && (
                  <Alert status="warning">
                    <Text fontWeight="semibold" fontSize="14px">
                      Submit assignments to trigger advance rate recalculations
                      across the portfolio. This will take several minutes.
                    </Text>
                  </Alert>
                )}

                {!canSubmitAssignment && (
                  <Alert status="info">
                    <Text fontWeight="semibold" fontSize="14px">
                      You are unauthorized to submit assignments.
                    </Text>
                  </Alert>
                )}

                <HStack mt="16px" mb="16px">
                  <Spacer />
                  <Button
                    color="white"
                    backgroundColor={colors.aurora[400]}
                    onClick={() => {
                      setIsSubmitModalOpen(true);
                    }}
                    isLoading={isSubmitLoading}
                    isDisabled={!canSubmitAssignment}
                  >
                    Submit Assignment Queue
                  </Button>
                </HStack>
              </>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const hideIfLoanAccountMissing = ({
  loanAccount,
  value,
}: {
  loanAccount: ValleyBankLoanAccountWithCollateralAndStats | undefined;
  value: Util.Maybe<string>;
}): string => {
  if (!loanAccount) {
    return 'MISSING';
  }
  return value || '-';
};

type PreviewHoldingsLoanAccountLevelCardsProps = {
  assignedReceivables: FinleyValleyBankResponseTapeRow[];
  collateralAccountReceivablesMap: Record<
    string,
    FinleyValleyBankResponseTapeRow[]
  >;
  loanAccount: ValleyBankLoanAccountWithCollateralAndStats | undefined;
  loanAccountNumber: string;
};
const PreviewHoldingsLoanAccountLevelCards = ({
  assignedReceivables,
  collateralAccountReceivablesMap,
  loanAccount,
  loanAccountNumber,
}: PreviewHoldingsLoanAccountLevelCardsProps) => {
  const collateralAccountNumbers = Array.from(
    new Set(assignedReceivables.map((r) => r.collateral_account_number)),
  );

  const collateralAccounts = generateCollateralAccountsForLoanAccount({
    loanAccountNumber,
    receivables: collateralAccountNumbers.flatMap(
      (collateralAccountNumber) =>
        collateralAccountReceivablesMap[collateralAccountNumber] || [],
    ),
    collateralAccountCustomers: [], // don't need owners because we don't need to show their full name, just their cif_num which is fetch off receivables
  });

  const stats = getCollateralAccountStats(collateralAccounts);
  const totalMarketValue = stats.totalMarketValue;
  const effectiveAdvanceRate = stats.effectiveAdvanceRate;
  const totalPreAllocationLendableValue = stats.totalPreAllocationLendableValue;
  const effectiveAllocationRate = stats.effectiveAllocationRate;

  const outstandingBalance = loanAccount?.principalBalance || 0;

  const marginAvailability = calculateMarginAvailability({
    loanOutstanding: outstandingBalance,
    collateralLendableValue: totalPreAllocationLendableValue,
  });

  const marginAvailabilityRate = calculateMarginAvailabilityRate({
    loanOutstanding: outstandingBalance,
    collateralLendableValue: totalPreAllocationLendableValue,
  });

  return (
    <Stack spacing="24px">
      <SimpleGrid columns={3} spacingY={4} spacingX={10}>
        <InfoCard
          title="Total Market Value"
          value={hideIfLoanAccountMissing({
            loanAccount,
            value: formatCurrencyAmount(totalMarketValue, Bank.Currency.USD),
          })}
          icon={<Icon icon="dollar" size="24px" />}
          flexGrow={1}
        />
        <InfoCard
          title="Effective Allocation Rate"
          value={hideIfLoanAccountMissing({
            loanAccount,
            value: percentageToPercentageLabel(
              effectiveAllocationRate * 100,
              2,
            ),
          })}
          icon={<Icon icon="percentage" size="24px" />}
          flexGrow={1}
        />

        <InfoCard
          title="Blended Advance Rate"
          value={hideIfLoanAccountMissing({
            loanAccount,
            value: percentageToPercentageLabel(effectiveAdvanceRate * 100, 2),
          })}
          icon={<Icon icon="percentage" size="24px" />}
          flexGrow={1}
        />
        <InfoCard
          title="Total Lendable Value"
          value={hideIfLoanAccountMissing({
            loanAccount,
            value: formatCurrencyAmount(
              totalPreAllocationLendableValue,
              Bank.Currency.USD,
            ),
          })}
          icon={<Icon icon="dollar" size="24px" />}
          flexGrow={1}
        />
        <InfoCard
          title="Total Outstanding Balance"
          value={hideIfLoanAccountMissing({
            loanAccount,
            value: formatCurrencyAmount(outstandingBalance, Bank.Currency.USD),
          })}
          icon={<Icon icon="dollar" size="24px" />}
          flexGrow={1}
        />
        <InfoCard
          title="Margin Availability"
          value={hideIfLoanAccountMissing({
            loanAccount,
            value: formatCurrencyAmount(marginAvailability, Bank.Currency.USD),
          })}
          icon={<Icon icon="dollar" size="24px" />}
          flexGrow={1}
        />
        <InfoCard
          title="Margin Availability Rate"
          value={hideIfLoanAccountMissing({
            loanAccount,
            value: percentageToPercentageLabel(marginAvailabilityRate * 100, 2),
          })}
          icon={<Icon icon="percentage" size="24px" />}
          flexGrow={1}
        />
      </SimpleGrid>
    </Stack>
  );
};

type CollateralAccountsFilterSelectProps = {
  value: string | undefined;
  onChange: (val: string) => void;
  receivables: FinleyValleyBankResponseTapeRow[];
};
const CollateralAccountsFilterSelect = ({
  value,
  onChange,
  receivables,
}: CollateralAccountsFilterSelectProps) => {
  const collateralAccountNums = new Set(
    receivables.map((r) => r.collateral_account_number),
  );
  const options = formatSelectOptions([
    ...collateralAccountNums,
    'All Collateral Accounts',
  ]);
  return (
    <Select
      options={options}
      onChange={(val) => {
        if (val) {
          onChange(val);
        }
      }}
      value={value}
    />
  );
};

type PreviewHoldingsProps = {
  previewData: FinleyValleyBankResponseTapeRow[];
  reportingDate: Util.FullDateStr;
};
const PreviewHoldings = ({
  previewData,
  reportingDate,
}: PreviewHoldingsProps) => {
  const toast = useToast();
  const rowsByLoanAccountNum = groupReceivableResponseTape({
    receivables: previewData,
    groupBy: 'loan_account_number',
  });

  // loan accounts need full set of collateral account receivables to accurately calculate statss
  const rowsByCollateralAccountNum = groupReceivableResponseTape({
    receivables: previewData,
    groupBy: 'collateral_account_number',
  });

  const loanAccountNums = Object.keys(rowsByLoanAccountNum);

  const {
    data: loanAccountsData,
  } = useGetValleyBankLoanAccountsByAccountNumsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: {
      reportingDate,
      isPreview: false,
      loanAccountNumbers: loanAccountNums,
    },
  });
  const loanAccountsMapByAccountNum = useMemo(() => {
    if (loanAccountsData) {
      const loanAccounts = parse.parseValleyBankLoanAccountsWithCollateralAndStatsArray(
        loanAccountsData.valleyBankLoanAccountByAccountNums,
      );
      return _.keyBy(loanAccounts, 'loanAccountNumber');
    }
    return null;
  }, [loanAccountsData]);

  const loanAccountOptions = loanAccountNums.map((n) => {
    return {
      label: `Loan Account ${n}`,
      value: n,
    };
  });

  const [selectedLoanAccountNum, setSelectedLoanAccountNum] = useState<string>(
    loanAccountNums[0],
  );

  const [
    collateralAccountToFilterBy,
    setCollateralAccountToFilterBy,
  ] = useState<string>('All Collateral Accounts');

  const receivablesToShow = useMemo(() => {
    if (collateralAccountToFilterBy === 'All Collateral Accounts') {
      return rowsByLoanAccountNum[selectedLoanAccountNum];
    }
    return rowsByLoanAccountNum[selectedLoanAccountNum].filter(
      (r) => r.collateral_account_number === collateralAccountToFilterBy,
    );
  }, [
    collateralAccountToFilterBy,
    rowsByLoanAccountNum,
    selectedLoanAccountNum,
  ]);

  if (previewData.length === 0) {
    return <Text>There is no data to preview.</Text>;
  }

  return (
    <Stack spacing="20px">
      {loanAccountNums.length > 1 && (
        <Box padding="25px" bgColor={colors.background[50]} mb="16px">
          <Text fontWeight="bold" mb="16px">
            Impacted Loan Accounts
          </Text>
          <Table bgColor="white" borderWidth="1px" size="sm">
            <Thead>
              <Tr>
                {IMPACTED_LOAN_ACCOUNT_HEADERS.map((header) => {
                  return (
                    <Th
                      key={header}
                      color="black"
                      fontSize="14px"
                      fontWeight={500}
                    >
                      {header}
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              <>
                {loanAccountNums.map((loanAccountNum) => {
                  const loanAccountAssignedReceivables =
                    rowsByLoanAccountNum[loanAccountNum] || [];
                  const collateralAccountNumbers = new Set(
                    loanAccountAssignedReceivables.map(
                      (r) => r.collateral_account_number,
                    ),
                  );
                  const collateralAccountsReceivables = Array.from(
                    collateralAccountNumbers,
                  ).flatMap(
                    (collateralAccountNumber) =>
                      rowsByCollateralAccountNum[collateralAccountNumber] || [],
                  );
                  const collatAccounts = generateCollateralAccountsForLoanAccount(
                    {
                      loanAccountNumber: loanAccountNum,
                      receivables: collateralAccountsReceivables,
                      collateralAccountCustomers: [],
                    },
                  );

                  const arrangementGroups = collatAccounts
                    .flatMap((c) =>
                      c.receivables.map((r) => r.arrangement_group),
                    )
                    .filter((r) => r !== null);

                  const arrangeMentGroupSet = new Set(arrangementGroups);

                  const totalLendableValue = getCollateralAccountsTotalLendableValue(
                    collatAccounts,
                  );

                  const loanAccount = loanAccountsMapByAccountNum
                    ? loanAccountsMapByAccountNum[loanAccountNum]
                    : undefined;

                  return (
                    <Tr key={loanAccountNum}>
                      <Td>{loanAccountNum}</Td>
                      <Td>{[...arrangeMentGroupSet].join(', ')}</Td>
                      <Td>
                        {hideIfLoanAccountMissing({
                          loanAccount,
                          value: formatCurrencyAmount(
                            loanAccount?.principalBalance || 0,
                            Bank.Currency.USD,
                          ),
                        })}
                      </Td>
                      <Td>
                        {hideIfLoanAccountMissing({
                          loanAccount,
                          value: formatCurrencyAmount(
                            getCollateralAccountsTotalMarketValue(
                              collatAccounts,
                            ),
                            Bank.Currency.USD,
                          ),
                        })}
                      </Td>
                      <Td>
                        {hideIfLoanAccountMissing({
                          loanAccount,
                          value: percentageToPercentageLabel(
                            (loanAccount?.effectiveAllocationRate ?? 0) * 100,
                            2,
                          ),
                        })}
                      </Td>
                      <Td>
                        {hideIfLoanAccountMissing({
                          loanAccount,
                          value: formatCurrencyAmount(
                            loanAccount?.totalLendableValue || 0,
                            Bank.Currency.USD,
                          ),
                        })}
                      </Td>

                      <Td>
                        {hideIfLoanAccountMissing({
                          loanAccount,
                          value: formatCurrencyAmount(
                            calculateMarginAvailability({
                              loanOutstanding:
                                loanAccount?.principalBalance || 0,
                              collateralLendableValue: totalLendableValue,
                            }),
                            Bank.Currency.USD,
                          ),
                        })}
                      </Td>
                      <Td>
                        {hideIfLoanAccountMissing({
                          loanAccount,
                          value: percentageToPercentageLabel(
                            calculateMarginAvailabilityRate({
                              collateralLendableValue:
                                loanAccount?.totalLendableValue || 0,
                              loanOutstanding:
                                loanAccount?.principalBalance || 0,
                            }) * 100,
                            2,
                          ),
                        })}
                      </Td>
                    </Tr>
                  );
                })}
              </>
            </Tbody>
          </Table>
        </Box>
      )}
      <HStack spacing="15px">
        <Box width="50%">
          <CollateralAccountsFilterSelect
            value={collateralAccountToFilterBy}
            receivables={rowsByLoanAccountNum[selectedLoanAccountNum]}
            onChange={(val) => {
              setCollateralAccountToFilterBy(val);
            }}
          />
        </Box>
        <Text>against</Text>
        {loanAccountNums.length === 1 ? (
          <Text fontWeight="bold">{`Loan Account ${selectedLoanAccountNum}`}</Text>
        ) : (
          <Select
            options={loanAccountOptions}
            value={selectedLoanAccountNum}
            onChange={(val) => {
              if (val) {
                setSelectedLoanAccountNum(val);
              }
            }}
          />
        )}
      </HStack>

      <Tabs>
        <TabList>
          <Tab {...tabProps}>Overview</Tab>
          <Tab {...tabProps}>Holdings</Tab>
        </TabList>

        <TabPanels>
          {/* Overview */}
          <TabPanel>
            <PreviewHoldingsLoanAccountLevelCards
              assignedReceivables={rowsByLoanAccountNum[selectedLoanAccountNum]}
              collateralAccountReceivablesMap={rowsByCollateralAccountNum}
              loanAccount={
                loanAccountsMapByAccountNum
                  ? loanAccountsMapByAccountNum[selectedLoanAccountNum]
                  : undefined
              }
              loanAccountNumber={selectedLoanAccountNum}
            />
            <Table bgColor="white" borderWidth="1px" size="md" mt="25px">
              <Thead>
                <Tr>
                  {PREVIEW_HOLDINGS_PER_LOAN_ACCOUNT_HEADERS.map((header) => {
                    return (
                      <Th
                        key={header}
                        color="black"
                        fontSize="16px"
                        fontWeight={500}
                      >
                        {header}
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>
                <>
                  {receivablesToShow.map((r) => {
                    return (
                      <Tr key={uuidv4()}>
                        <Td>{getCollateralDescription(r)}</Td>
                        <Td>{r.collateral_account_number}</Td>
                        <Td>{getSecurityType(r)}</Td>
                        <Td>
                          {formatCurrencyAmount(
                            getPreAllocationMarketValue(r),
                            Bank.Currency.USD,
                          )}
                        </Td>
                        <Td>
                          {percentageToPercentageLabel(
                            getAdvanceRate(r) * 100,
                            2,
                          )}
                        </Td>
                        <Td>
                          {formatCurrencyAmount(
                            getPreAllocationLendableValue(r),
                            Bank.Currency.USD,
                          )}
                        </Td>
                        <Td>{getQuantity(r) ?? '-'}</Td>
                      </Tr>
                    );
                  })}
                </>
              </Tbody>
            </Table>
          </TabPanel>

          {/* Holdings */}
          <TabPanel>
            <VStack w="100%">
              <CollateralTable
                collateralAccounts={generateCollateralAccounts({
                  receivables: rowsByLoanAccountNum[selectedLoanAccountNum],
                  collateralAccountCustomers: [], // don't need owners because we don't need to show their full name, just their cif_num which is fetch off receivables
                })}
                isDataLoading={false}
                isDataError={false}
                showAllocation
                pageType="assignment"
              />
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Stack>
  );
};

type ShowAssignmentEventLogProps = {
  data: GetValleyBankAssignmentEventLogQueryGQLType;
};
const ShowAssignmentEventLog = ({ data }: ShowAssignmentEventLogProps) => {
  return (
    <Stack mt="20px" spacing="20px">
      {data.valleyBankAssignmentEventLog.length === 0 ? (
        <Text fontSize="14px" fontWeight="semibold">
          No assignments have been submitted.
        </Text>
      ) : (
        data.valleyBankAssignmentEventLog.map((r) => {
          return (
            <HStack width="full" key={r.id}>
              <Icon icon={r.action === 'assign' ? 'sliders' : 'link-slash'} />
              <Text
                fontSize="14px"
                fontWeight="semibold"
              >{`${r.userFirstName} ${r.userLastName} queued an ${r.action}ment for collateral account ${r.collateralAccountNumber} to loan account `}</Text>
              <LoanAccountSummaryLink loanAccountId={r.loanAccountNumber}>
                <Text fontSize="14px">{r.loanAccountNumber}</Text>
              </LoanAccountSummaryLink>
              <Text>.</Text>
              <Spacer />

              <Text
                fontSize="14px"
                color={colors.black[70]}
              >{`Submitted ${commonDate.epochToLocaleDateString(
                r.created_at,
                undefined,
              )}`}</Text>
            </HStack>
          );
        })
      )}
    </Stack>
  );
};

export const Assignment = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { permissions } = useUserRoleTypePermissionsContext();
  const { setIsAssignmentSubmitted } = useDataMetadataContext();

  const query = useQueryParams();
  const isQueueOpen = query.get(ASSIGNMENT_URL_PARAM);

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  // keeps track of any collateral accounts that have been selected (all checkbox true in first accordion table)
  const [selectedCollateralAccounts, setSelectedCollateralAccounts] = useState<
    Map<string, string> // key = collateralAccountNum, value = cifNum
  >(new Map());

  // keeps track of all collateral accounts you can assign (all rows in first accordion table)
  const [collateralAccountsToAssign, setCollateralAccountsToAssign] = useState<
    string[]
  >([]);

  // assignment queue drawer
  const [
    isAssignmentQueueDrawerOpen,
    setIsAssignmentQueueDrawerOpen,
  ] = useState<boolean>(
    isQueueOpen ? isQueueOpen.toLowerCase() === 'true' : false,
  );

  // assignment queue gql
  const {
    data: assignmentQueueData,
    refetch: refetchAssignmentQueueData,
    loading: isAssignmentQueueDataLoading,
  } = useGetValleyBankAssignmentQueueQuery({
    fetchPolicy: 'network-only',
  });

  const queue = useMemo(() => {
    if (assignmentQueueData) {
      return assignmentQueueData.valleyBankAssignmentQueue;
    }
    return null;
  }, [assignmentQueueData]);

  const [
    addToValleyBankAssignmentQueueMutation,
    { loading: isAddToValleyBankAssignmentQueueMutationLoading },
  ] = useAddToValleyBankAssignmentQueueMutation({
    fetchPolicy: 'network-only',
    onCompleted(data) {
      // mutation returns back list of errors
      // surface any errors here
      if (data.valleyBankAddToAssignmentsQueue.length > 0) {
        if (!toast.isActive(errorToastId)) {
          toast({
            ...errorToastConfig,
            title: 'Errors on adding to assignment queue',
            description: data.valleyBankAddToAssignmentsQueue.join('; '),
          });
        }
      }
    },
    onError(error) {
      if (!toast.isActive(errorToastId)) {
        toast({
          ...errorToastConfig,
          title: 'Errors',
          description: convertErrorToString(error),
        });
      }
    },
  });

  const [
    deleteFromValleyBankAssignmentQueueMutation,
    { loading: isDeleteFromValleyBankAssignmentQueueMutationLoading },
  ] = useDeleteFromValleyBankAssignmentQueueMutation({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Successfully deleted assignment from queue.',
      });
    },
    onError(error) {
      toast({
        ...errorToastConfig,
        title: 'Errors on deleting from assignment queue',
        description: convertErrorToString(error),
      });
    },
  });

  const [
    submitValleyBankAssignmentQueueMutation,
    { loading: isSubmitValleyBankAssignmentQueueMutationLoading },
  ] = useSubmitValleyBankAssignmentQueueMutation({
    fetchPolicy: 'network-only',
    onCompleted(data) {
      // mutation returns back list of errors
      // surface any errors here
      if (data.submitValleyBankAssignmentQueue.length > 0) {
        if (!toast.isActive(errorToastId)) {
          toast({
            ...errorToastConfig,
            title: 'Errors on submitting the assignment queue',
            description: data.submitValleyBankAssignmentQueue.join('; '),
          });
        }
      } else {
        toast({
          ...successToastConfig,
          title: 'Successfully submitted assignment queue.',
        });
        setIsAssignmentSubmitted(true);

        navigate('/valley-bank/loan-accounts');
      }
    },
    onError(error) {
      if (!toast.isActive(errorToastId)) {
        toast({
          ...errorToastConfig,
          title: 'Errors',
          description: convertErrorToString(error),
        });
      }
    },
  });

  const {
    data: eventLogData,
    refetch: refetchEventLog,
  } = useGetValleyBankAssignmentEventLogQuery({
    fetchPolicy: 'network-only',
  });

  const { data: assignmentData } = useGetValleyBankAssignmentDeviationQuery({
    fetchPolicy: 'network-only',
  });

  const collateralAccountsMap = useMemo(() => {
    if (assignmentData) {
      return _.keyBy(
        assignmentData.valleyBankAssignmentDeviations.collateralAccounts,
        'collateralAccountNum',
      );
    }
    return {};
  }, [assignmentData]);

  const [
    valleyBankLoanAccountsByCifNums,
    { data: loanAccsByCifNumData },
  ] = useGetValleyBankAssociatedLoanAccountsByCifNumsLazyQueryGQLType({
    fetchPolicy: 'no-cache',
  });

  const [loanAccountsLoading, setLoanAccountsLoading] = useState<boolean>(
    false,
  );
  useEffect(() => {
    if (assignmentData) {
      const cifNums = [...selectedCollateralAccounts.values()];
      setLoanAccountsLoading(true);
      valleyBankLoanAccountsByCifNums({
        variables: {
          isPreview: false,
          reportingDate:
            assignmentData.valleyBankAssignmentDeviations.reportingDate,
          cifNums,
        },
      });
    }
  }, [
    assignmentData,
    selectedCollateralAccounts,
    valleyBankLoanAccountsByCifNums,
  ]);

  const assignableLoanAccounts = useMemo(() => {
    if (loanAccsByCifNumData) {
      const loanAccsAsJSON =
        loanAccsByCifNumData.valleyBankAssociatedLoanAccountsByCifNums;

      setLoanAccountsLoading(false);
      return parse.parseValleyBankLoanAccountsWithCollateralAndStatsArray(
        loanAccsAsJSON,
      );
    }
    return [];
  }, [loanAccsByCifNumData]);

  // loan account num selected that we want to preview for
  const [previewLoanAccountNum, setPreviewLoanAccountNum] = useState<string>();

  // prevent duplicate CA <> LA mappings from being added
  const isDuplicateSelected = useMemo(() => {
    if (previewLoanAccountNum) {
      for (const selectedCollatAccNum of selectedCollateralAccounts.keys()) {
        if (queue) {
          const existingAssignment = queue.find(
            (q) =>
              q.collateralAccountNumber === selectedCollatAccNum &&
              q.loanAccountNumber === previewLoanAccountNum,
          );
          if (existingAssignment) {
            return 'This assignment is already in the queue. Please select a different account to assign.';
          }
        }
        if (
          collateralAccountsMap[
            selectedCollatAccNum
          ].assignedLoanAccountNums.includes(previewLoanAccountNum)
        ) {
          return 'This assignment has already been submitted. Please select a different account to assign.';
        }
      }
    }
    return '';
  }, [
    collateralAccountsMap,
    previewLoanAccountNum,
    selectedCollateralAccounts,
    queue,
  ]);

  // assignment preview logic
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<
    FinleyValleyBankResponseTapeRow[] | null
  >(null);

  const [
    valleyBankAssignmentPreviewRRT,
  ] = useGetValleyBankAssignmentPreviewRrtLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      if (
        res &&
        !isDuplicateSelected &&
        selectedCollateralAccounts.size > 0 &&
        previewLoanAccountNum
      ) {
        const result = parse.parseValleyBankAssignmentPreviewRRT(
          res.valleyBankAssignmentPreviewRRT,
        );
        if (result.errors.length === 0) {
          setPreviewData(result.rows);
        } else {
          // @vb this throws an error every time try to preview holdings for new assignmenst when unassignments are already in the queue
          // temp silence until preview rrt supports unassignments
          // toast({
          //   ...errorToastConfig,
          //   title: 'Preview Holdings Errors',
          //   description: result.errors,
          // });
          setPreviewData(null);
        }
      } else {
        setPreviewData(null);
      }
      setIsPreviewLoading(false);
    },
  });

  useEffect(() => {
    const selectedCollateralAccNumsList = [
      ...selectedCollateralAccounts.keys(),
    ];
    if (assignmentData && previewLoanAccountNum && !loanAccountsLoading) {
      const selectedToPreview = selectedCollateralAccNumsList.map(
        (collatAccNum) => {
          return {
            loanAccountNum: previewLoanAccountNum,
            collateralAccountNum: collatAccNum,
            collateralAccountType:
              collateralAccountsMap[collatAccNum].collateralAccountType,
            action: 'assign' as const,
          };
        },
      );

      // only generate previews if we have any new selections
      if (selectedToPreview.length > 0) {
        // combine current selected map with whatever is in summary
        const queueToPreview = queue
          ? queue.map((q) => {
              return {
                loanAccountNum: q.loanAccountNumber,
                collateralAccountNum: q.collateralAccountNumber,
                collateralAccountType: q.collateralAccountType,
                action: q.action,
              };
            })
          : [];
        const assignmentsToPreview = [...selectedToPreview, ...queueToPreview];

        // clear preview preview data + call lazy query
        setPreviewData(null);
        setIsPreviewLoading(true);
        valleyBankAssignmentPreviewRRT({
          variables: {
            reportingDate:
              assignmentData.valleyBankAssignmentDeviations.reportingDate,
            previewLoanAccountNum,
            assignmentsToPreview,
          },
        });
      }
    }
  }, [
    assignmentData,
    collateralAccountsMap,
    loanAccountsLoading,
    previewLoanAccountNum,
    selectedCollateralAccounts,
    queue,
    valleyBankAssignmentPreviewRRT,
  ]);

  if (
    !assignmentData ||
    !assignmentData.valleyBankAssignmentDeviations ||
    !assignmentQueueData ||
    !eventLogData ||
    !queue ||
    !userData ||
    !userData.user
  ) {
    return <Skeleton height="900px" data-testid="assignment-loading" />;
  }

  const reportingDate =
    assignmentData.valleyBankAssignmentDeviations.reportingDate;

  const userId = userData.user.id;

  return (
    <>
      <ViewAssignmentQueueDrawer
        isOpen={isAssignmentQueueDrawerOpen}
        isSubmitLoading={
          isSubmitValleyBankAssignmentQueueMutationLoading ||
          isAssignmentQueueDataLoading
        }
        isDeleteLoading={
          isDeleteFromValleyBankAssignmentQueueMutationLoading ||
          isAssignmentQueueDataLoading
        }
        queue={queue}
        onClose={() => {
          setIsAssignmentQueueDrawerOpen(false);
        }}
        onSubmit={async () => {
          setIsAssignmentQueueDrawerOpen(false);
          // clear existing preview data
          setPreviewData(null);
          await submitValleyBankAssignmentQueueMutation();
          await refetchAssignmentQueueData();
          await refetchEventLog();
        }}
        onDelete={async (id: string) => {
          // clear existing preview data
          setPreviewData(null);
          await deleteFromValleyBankAssignmentQueueMutation({
            variables: {
              assignmentQueueId: id,
            },
          });
          await refetchAssignmentQueueData();
        }}
        canSubmitAssignment={permissions.assignmentQueueSubmit}
        canEditAssignmentQueue={permissions.assignmentQueueEdit}
      />
      <HStack bgColor="gray.50" padding="16px" width="full">
        <Spacer />
        <Button
          variant="link"
          colorScheme="blue"
          fontSize="sm"
          pr={2}
          onClick={() => {
            setIsAssignmentQueueDrawerOpen(true);
          }}
        >
          {queue.length > 0 ? `View Queue (${queue.length})` : 'View Queue'}
        </Button>
      </HStack>
      <Box padding={dashboardRoutePadding} pt="16px">
        {isDuplicateSelected.length > 0 && (
          <Alert status="error" mt="16px" mb="16px">
            <Text>{isDuplicateSelected}</Text>
          </Alert>
        )}
        {/* @vb there is currently no role that has read only permissions for assignment, this is a placeholder in case there is a new role in the future that would allow read only */}
        {permissions.assignmentQueueRead &&
          !permissions.assignmentQueueEdit &&
          !permissions.assignmentQueueSubmit && (
            <Alert status="info" m="16px 0">
              <Text>
                This is a read only version of Assignment. You will be unable to
                add to the assignment queue nor submit assignments.
              </Text>
            </Alert>
          )}
        <Heading as="h1" mt="16px" mb="16px" fontSize="36px" fontWeight={600}>
          Assignment
        </Heading>
        <Text fontSize="16px" mb="24px" color={colors.black[70]}>
          Choose one or more Collateral Accounts from the list to assign to a
          Loan Account. If the preferred Collateral Account isn&apos;t visible,
          you can search for it. Your assignment will be saved until it is
          submitted.
        </Text>

        <Tabs>
          <TabList>
            <Tab {...tabProps}>Assign</Tab>
            <Tab {...tabProps}>Event Log</Tab>
          </TabList>

          <TabPanels>
            {/* ASSIGN */}

            <TabPanel>
              {/* Collateral Accounts */}
              <Accordion
                defaultIndex={[0, 1, 2]}
                allowMultiple
                sx={{
                  '& .chakra-collapse': { overflow: 'initial !important' },
                }}
              >
                <AccordionItem
                  borderWidth="1px"
                  borderRadius="8px"
                  mb="16px"
                  padding="8px"
                >
                  <Heading as="h2" paddingY="8px">
                    <AccordionButton _focus={{ boxShadow: 'none' }}>
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontSize="20px"
                        fontWeight={700}
                      >
                        Add Collateral Accounts
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </Heading>
                  <AccordionPanel pb={4} mt="16px">
                    <ManuallyAddCollateralAccounts
                      searchOptions={formatSelectOptions(
                        _.keys(collateralAccountsMap).filter(
                          (k) => !collateralAccountsToAssign.includes(k), // don't pass in accounts that are already in the table
                        ),
                      )}
                      onSubmit={(val) => {
                        setCollateralAccountsToAssign((curr) => {
                          if (!curr.includes(val)) {
                            // prevent duplicate adding here too
                            return [val, ...curr];
                          }
                          return curr;
                        });
                      }}
                    />
                  </AccordionPanel>
                </AccordionItem>
                <AccordionItem
                  borderWidth="1px"
                  borderRadius="8px"
                  mb="16px"
                  padding="8px"
                >
                  <Heading as="h2" paddingY="8px">
                    <AccordionButton _focus={{ boxShadow: 'none' }}>
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontSize="20px"
                        fontWeight={700}
                      >
                        Select Collateral Accounts to Assign
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </Heading>
                  <AccordionPanel pb={4} mt="16px">
                    <Box
                      maxHeight="300px"
                      overflowY="auto"
                      mb="16px"
                      borderWidth="1px"
                    >
                      <Table>
                        <Thead>
                          <Tr>
                            {COLLATERAL_ACCOUNT_HEADERS.map((header) => {
                              return (
                                <Th
                                  key={header}
                                  color="black"
                                  fontSize="16px"
                                  fontWeight={500}
                                >
                                  {header}
                                </Th>
                              );
                            })}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <>
                            {collateralAccountsToAssign.length === 0 ? (
                              <Tr>
                                <Td
                                  colSpan={4}
                                  color={colors.black[70]}
                                  fontSize="16px"
                                >
                                  All pledged indicators have been assigned.
                                </Td>
                              </Tr>
                            ) : (
                              collateralAccountsToAssign.map((collatNum) => {
                                const collat = collateralAccountsMap[collatNum];
                                return (
                                  <Tr key={collat.collateralAccountNum}>
                                    <Td>
                                      <Checkbox
                                        isChecked={selectedCollateralAccounts.has(
                                          collatNum,
                                        )}
                                        onChange={(_e) => {
                                          setSelectedCollateralAccounts(
                                            (curr) => {
                                              const copy = _.cloneDeep(curr);
                                              if (copy.has(collatNum)) {
                                                // delete
                                                copy.delete(collatNum);
                                              } else {
                                                // add
                                                copy.set(
                                                  collatNum,
                                                  collat.cifNum,
                                                );
                                              }
                                              return copy;
                                            },
                                          );
                                          setPreviewLoanAccountNum(undefined);
                                        }}
                                      >
                                        {collat.collateralAccountNum}
                                      </Checkbox>
                                    </Td>
                                    <Td>
                                      {_.startCase(
                                        collat.fullName.toLowerCase(),
                                      )}
                                    </Td>
                                    <Td>{collat.arrangmentGroup ?? '-'}</Td>
                                    <Td overflowX="auto" flexGrow={0}>
                                      {collat.assignedLoanAccountNums.length > 0
                                        ? collat.assignedLoanAccountNums.join(
                                            ', ',
                                          )
                                        : 'Unassigned'}
                                    </Td>
                                  </Tr>
                                );
                              })
                            )}
                          </>
                        </Tbody>
                      </Table>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem
                  borderWidth="1px"
                  borderRadius="8px"
                  mb="16px"
                  padding="8px"
                >
                  <Heading as="h2" paddingY="8px">
                    <AccordionButton _focus={{ boxShadow: 'none' }}>
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontSize="20px"
                        fontWeight={700}
                      >
                        Select Loan Accounts to Assign
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </Heading>
                  <AccordionPanel pb={4}>
                    <Box
                      minHeight="min-content"
                      maxHeight="350px"
                      overflowY="auto"
                      mb="16px"
                      borderWidth="1px"
                    >
                      <Table>
                        <Thead>
                          <Tr>
                            {ASSIGNABLE_LOAN_ACCOUNT_HEADERS.map((header) => {
                              return (
                                <Th
                                  key={header}
                                  color="black"
                                  fontSize="16px"
                                  fontWeight={500}
                                >
                                  {header}
                                </Th>
                              );
                            })}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <>
                            {loanAccountsLoading && (
                              <Tr>
                                <Td colSpan={7}>
                                  <Skeleton height="50px" width="full" />
                                </Td>
                              </Tr>
                            )}
                            {!loanAccountsLoading &&
                              assignableLoanAccounts.length === 0 && (
                                <Tr>
                                  <Td
                                    colSpan={4}
                                    color={colors.black[70]}
                                    fontSize="16px"
                                  >
                                    {selectedCollateralAccounts.size === 0
                                      ? 'Select a collateral account to see loan accounts'
                                      : 'No loan accounts are associated with the selected collateral account(s).'}
                                  </Td>
                                </Tr>
                              )}
                            {!loanAccountsLoading &&
                              assignableLoanAccounts.length > 0 &&
                              assignableLoanAccounts.map((loanAccount) => {
                                const borrower = loanAccount.loanParties.find(
                                  (party) =>
                                    party.cif_num === loanAccount.cifNum,
                                );

                                return (
                                  <Tr key={loanAccount.loanAccountNumber}>
                                    <Td>
                                      <HStack w="100%" spacing="8px">
                                        <Radio
                                          isChecked={
                                            previewLoanAccountNum ===
                                            loanAccount.loanAccountNumber
                                          }
                                          onChange={(_e) => {
                                            setPreviewLoanAccountNum(
                                              loanAccount.loanAccountNumber,
                                            );
                                          }}
                                        />
                                        <LoanAccountSummaryLink
                                          loanAccountId={
                                            loanAccount.loanAccountNumber
                                          }
                                        >
                                          {loanAccount.loanAccountNumber}
                                        </LoanAccountSummaryLink>
                                      </HStack>
                                    </Td>
                                    <Td>
                                      <CustomerSummaryLink
                                        cifnum={loanAccount.cifNum}
                                      >
                                        {borrower?.full_name ||
                                          loanAccount.cifNum}
                                      </CustomerSummaryLink>
                                    </Td>
                                    <Td>
                                      {loanAccount.arrangementGroupId ? (
                                        <ArrangementGroupSummaryLink
                                          arrangementGroup={
                                            loanAccount.arrangementGroupId
                                          }
                                        >
                                          {loanAccount.arrangementGroupId}
                                        </ArrangementGroupSummaryLink>
                                      ) : (
                                        '-'
                                      )}
                                    </Td>
                                    <Td>
                                      {formatCurrencyAmount(
                                        loanAccount.principalBalance,
                                        Bank.Currency.USD,
                                      )}
                                    </Td>
                                    <Td>
                                      {formatCurrencyAmount(
                                        loanAccount.totalLendableValue,
                                        Bank.Currency.USD,
                                      )}
                                    </Td>
                                    <Td>
                                      {formatCurrencyAmount(
                                        calculateMarginAvailability({
                                          collateralLendableValue:
                                            loanAccount.totalLendableValue,
                                          loanOutstanding:
                                            loanAccount.principalBalance,
                                        }),
                                        Bank.Currency.USD,
                                      )}
                                    </Td>
                                    <Td>
                                      {percentageToPercentageLabel(
                                        loanAccount.marginAvailabilityRate *
                                          100,
                                        2,
                                      )}
                                    </Td>
                                  </Tr>
                                );
                              })}
                          </>
                        </Tbody>
                      </Table>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem
                  borderWidth="1px"
                  borderRadius="8px"
                  mb="16px"
                  padding="8px"
                >
                  <Heading as="h2" paddingY="8px">
                    <AccordionButton _focus={{ boxShadow: 'none' }}>
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontSize="20px"
                        fontWeight={700}
                      >
                        Preview Holdings For Loan Accounts
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </Heading>
                  <AccordionPanel pb={4}>
                    <Box
                      minHeight="min-content"
                      maxHeight="750px"
                      overflowY="auto"
                      mb="16px"
                    >
                      {isPreviewLoading && (
                        <Skeleton height="300px" width="full" />
                      )}
                      {/* If there is no data and it also isn't loading. This means the user hasn't selected CAs and LA. */}
                      {!isPreviewLoading && !previewData && (
                        <Text>
                          Select collateral accounts to assign to a loan account
                          to preview holdings.
                        </Text>
                      )}
                      {!isPreviewLoading && previewData && (
                        <PreviewHoldings
                          previewData={previewData}
                          reportingDate={reportingDate}
                        />
                      )}
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>

              <HStack>
                <Spacer />
                <Button
                  color="white"
                  backgroundColor={colors.aurora[400]}
                  isDisabled={
                    isDuplicateSelected.length > 0 ||
                    !previewLoanAccountNum ||
                    assignableLoanAccounts.length === 0 ||
                    !permissions.assignmentQueueEdit
                  }
                  isLoading={
                    isAssignmentQueueDataLoading ||
                    isAddToValleyBankAssignmentQueueMutationLoading
                  }
                  onClick={async () => {
                    if (previewLoanAccountNum) {
                      const selectedCollateralAccNumsList = [
                        ...selectedCollateralAccounts.keys(),
                      ];
                      const newVals: ValleyBankAddToAssignmentQueueGQLType[] = selectedCollateralAccNumsList.map(
                        (collatAccNum) => {
                          return {
                            loanAccountNumber: previewLoanAccountNum,
                            collateralAccountNumber: collatAccNum,
                            collateralAccountType:
                              collateralAccountsMap[collatAccNum]
                                .collateralAccountType,
                            action: 'assign',
                            userId,
                          };
                        },
                      );

                      // add to summary cart
                      await addToValleyBankAssignmentQueueMutation({
                        variables: {
                          records: newVals,
                        },
                      });
                      await refetchAssignmentQueueData();

                      // remove from 'collateral accounts to assign' table to prevent duplicates
                      setCollateralAccountsToAssign((curr) =>
                        curr.filter(
                          (c) => !selectedCollateralAccNumsList.includes(c),
                        ),
                      );
                      setSelectedCollateralAccounts(new Map());
                      setPreviewLoanAccountNum(undefined);

                      // open drawer
                      setIsAssignmentQueueDrawerOpen(true);
                    }
                  }}
                >
                  Add to Assignment
                </Button>
              </HStack>
            </TabPanel>

            {/* EVENT LOG */}

            <TabPanel>
              <ShowAssignmentEventLog data={eventLogData} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </>
  );
};
